import { PublicClientApplication, LogLevel, type Configuration } from "@azure/msal-browser";

const tenantId = import.meta.env.VITE_AZURE_AD_TENANT_ID;
const clientId = import.meta.env.VITE_AZURE_AD_CLIENT_ID;

/** Requested at sign-in and reused (silently refreshed) for API calls. */
export const apiScope = import.meta.env.VITE_AZURE_AD_API_SCOPE;

/**
 * True only when every required Azure AD env var is present and isn't still
 * a "<placeholder>" from .env.example. main.tsx checks this BEFORE calling
 * any MSAL API.
 *
 * Why this matters: constructing PublicClientApplication with an
 * undefined/placeholder clientId and then calling loginRedirect() is exactly
 * what leaves MSAL's sessionStorage "interaction in progress" flag stuck
 * forever - MSAL sets that flag as the first step of preflightInteractiveRequest,
 * *then* validates the config and throws, so the flag never gets cleared by
 * the real redirect (which never happens). Every subsequent click then fails
 * with BrowserAuthError: interaction_in_progress, even though the actual
 * root cause was the missing config, not a real concurrent interaction. The
 * fix is to never make that call with bad config in the first place - see
 * main.tsx / ConfigMissingScreen.
 */
export const isMsalConfigured = Boolean(
  tenantId &&
    clientId &&
    apiScope &&
    !tenantId.startsWith("<") &&
    !clientId.startsWith("<") &&
    !apiScope.startsWith("<")
);

const msalConfig: Configuration = {
  auth: {
    clientId: clientId ?? "",
    authority: `https://login.microsoftonline.com/${tenantId || "common"}`,
    // Resolved at runtime against the actual host (localhost:5173 in dev,
    // your real domain in prod) rather than hard-coded, so the same build
    // works across environments as long as that origin is registered as a
    // redirect URI on the SPA app registration.
    redirectUri: typeof window !== "undefined" ? window.location.origin : "/",
    postLogoutRedirectUri: typeof window !== "undefined" ? window.location.origin : "/",
    // false (not MSAL's default true) - true makes MSAL auto-navigate back to
    // whichever page loginRedirect() was called from (e.g. "/login") after
    // processing the response, which bounces the user right back to the
    // login screen the moment the landing page introduced a dedicated
    // "/login" URL (before that, login was only ever initiated from "/",
    // so this went unnoticed). main.tsx already handles the post-redirect
    // state explicitly (handleRedirectPromise + setActiveAccount) at
    // redirectUri, so MSAL doesn't need to navigate anywhere on top of that.
    navigateToLoginRequestUrl: false
  },
  cache: {
    // sessionStorage keeps tokens out of long-lived localStorage while still
    // surviving a page refresh within the same tab.
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error("[MSAL]", message);
      },
      logLevel: LogLevel.Warning
    }
  }
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const loginRequest = {
  scopes: apiScope ? [apiScope, "User.Read"] : ["User.Read"]
};

/**
 * Clears MSAL's per-clientId "interaction in progress" flag from
 * sessionStorage. Safe to call at any time, including before initialize():
 * handleRedirectPromise() re-establishes the flag correctly if we're
 * actually in the middle of a real redirect right now. This is the
 * documented community workaround for a flag that survives an interrupted
 * interactive call - a page reload mid-redirect, dev-server HMR reloading
 * the module during the AAD round trip, or (the concrete cause in this app
 * before .env.local was configured) loginRedirect() throwing on invalid
 * config after the flag was already set.
 */
export function clearStuckInteractionState() {
  Object.keys(sessionStorage)
    .filter((key) => key.includes("interaction.status"))
    .forEach((key) => sessionStorage.removeItem(key));
}

